import React, { useMemo, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Chip,
  Tooltip,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import HPButton from "@components/common/Button";
import HPIconButton from "@components/common/IconButton";
import type { WidgetRollup } from "@models/Rollup";
import CategoryRollupBuilder, {
  type BarOption,
} from "@components/charts/CategoryRollupBuilder";

interface CategoryRollupsSectionProps {
  categoryRollups: WidgetRollup[];
  barOptions: BarOption[];
  onCreateRollup: (rollup: WidgetRollup) => void;
  onUpdateRollup: (rollup: WidgetRollup) => void;
  onDeleteRollup: (rollupId: string) => void;
  title?: string;
  itemLabel?: string;
  nameLabel?: string;
  onBuilderOpenChange?: (isOpen: boolean) => void;
  bordered?: boolean;
  disabled?: boolean;
  disabledMessage?: string;
  // Disables only the "Add New Rollup" control (and shows addDisabledMessage) while
  // keeping existing rollups editable/deletable. Used for weight-mode gating.
  addDisabled?: boolean;
  addDisabledMessage?: string;
  // Source labels that cannot be used as a source, mapped to the reason.
  ineligibleSourceLabels?: Map<string, string>;
  minSelectionMessage?: string;
  /** When true, shows a caveat that SUM_SELECTED may exceed 100% (contexts where a
   * respondent can contribute to more than one selected source, e.g. Multiple-choice). */
  overlapCapable?: boolean;
}

const CategoryRollupsSection: React.FC<CategoryRollupsSectionProps> = ({
  categoryRollups,
  barOptions,
  onCreateRollup,
  onUpdateRollup,
  onDeleteRollup,
  title = "Rollups",
  itemLabel = "bars",
  nameLabel,
  onBuilderOpenChange,
  bordered = true,
  disabled = false,
  disabledMessage,
  addDisabled = false,
  addDisabledMessage,
  ineligibleSourceLabels,
  minSelectionMessage,
  overlapCapable = false,
}) => {
  const [isBuilderOpen, setIsBuilderOpen] = useState(false);
  const [editingRollupId, setEditingRollupId] = useState<string | null>(null);
  const [builderName, setBuilderName] = useState("");
  const [builderSelectedLabels, setBuilderSelectedLabels] = useState<string[]>(
    [],
  );

  const openBuilder = () => {
    setIsBuilderOpen(true);
    onBuilderOpenChange?.(true);
  };

  const resetBuilder = () => {
    setIsBuilderOpen(false);
    setEditingRollupId(null);
    setBuilderName("");
    setBuilderSelectedLabels([]);
    onBuilderOpenChange?.(false);
  };

  // Source labels claimed by OTHER rollups, mapped to the claiming rollup's name —
  // excludes the one being edited so its own bars stay selectable.
  const claimedSourceLabelToRollupName = useMemo(() => {
    const claimed = new Map<string, string>();
    categoryRollups.forEach((r) => {
      if (r.id !== editingRollupId) {
        r.sourceCategoryLabels.forEach((l) =>
          claimed.set(l, r.name || "another rollup"),
        );
      }
    });
    return claimed;
  }, [categoryRollups, editingRollupId]);

  const claimedSourceLabels = useMemo(
    () => new Set(claimedSourceLabelToRollupName.keys()),
    [claimedSourceLabelToRollupName],
  );

  // Map from sourceLabel (variableId) → displayLabel for chip rendering.
  const idToDisplayLabel = useMemo(() => {
    const map = new Map<string, string>();
    barOptions.forEach((b) => map.set(b.sourceLabel, b.displayLabel));
    return map;
  }, [barOptions]);

  // Name conflict set: unmodified (original) source labels + other rollup names
  // (excludes the rollup being edited so it doesn't conflict with itself).
  const existingNames = useMemo(() => {
    const names = new Set<string>();
    barOptions.forEach((b) =>
      names.add((b.originalLabel ?? b.displayLabel).toLowerCase()),
    );
    categoryRollups.forEach((r) => {
      if (r.id !== editingRollupId && r.name) {
        names.add(r.name.toLowerCase());
      }
    });
    return names;
  }, [barOptions, categoryRollups, editingRollupId]);

  // Bars not yet claimed by any rollup — determines whether a new rollup can be created.
  const eligibleBarCount = useMemo(
    () =>
      barOptions.filter((b) => !claimedSourceLabels.has(b.sourceLabel)).length,
    [barOptions, claimedSourceLabels],
  );

  const handleOpenBuilder = () => {
    setEditingRollupId(null);
    setBuilderName("");
    setBuilderSelectedLabels([]);
    openBuilder();
  };

  const handleEditRollup = (rollup: WidgetRollup) => {
    setEditingRollupId(rollup.id);
    setBuilderName(rollup.name ?? "");
    setBuilderSelectedLabels([...rollup.sourceCategoryLabels]);
    openBuilder();
  };

  const handleSave = () => {
    const rollup: WidgetRollup = {
      id: editingRollupId ?? crypto.randomUUID(),
      name: builderName.trim(),
      calculationType: "SUM_SELECTED",
      sourceCategoryLabels: builderSelectedLabels,
    };
    if (editingRollupId) {
      onUpdateRollup(rollup);
    } else {
      onCreateRollup(rollup);
    }
    resetBuilder();
  };

  const content = (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      {categoryRollups.length > 0 && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
          {categoryRollups.map((rollup) => {
            const isCurrentlyEditing = editingRollupId === rollup.id;
            return (
              <Box
                key={rollup.id}
                sx={{
                  p: 1.5,
                  border: isCurrentlyEditing
                    ? "1px solid var(--color-hp-primary, #1976d2)"
                    : "1px solid #eee",
                  borderRadius: 1,
                  display: "flex",
                  flexDirection: "column",
                  gap: 1,
                  opacity: isBuilderOpen && !isCurrentlyEditing ? 0.5 : 1,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  {rollup.name && (
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {rollup.name}
                    </Typography>
                  )}
                  {!disabled && (
                    <Box sx={{ display: "flex", gap: 0.5, ml: "auto" }}>
                      <Tooltip title="Edit rollup">
                        <span>
                          <HPIconButton
                            ariaLabel="Edit rollup"
                            onClick={() => handleEditRollup(rollup)}
                          >
                            <EditIcon fontSize="small" />
                          </HPIconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title="Delete rollup">
                        <span>
                          <HPIconButton
                            ariaLabel="Delete rollup"
                            onClick={() => onDeleteRollup(rollup.id)}
                          >
                            <DeleteIcon fontSize="small" />
                          </HPIconButton>
                        </span>
                      </Tooltip>
                    </Box>
                  )}
                </Box>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                  {rollup.sourceCategoryLabels.map((label) => (
                    <Chip
                      key={label}
                      label={idToDisplayLabel.get(label) ?? label}
                      size="small"
                    />
                  ))}
                </Box>
                <Typography variant="caption" color="text.secondary">
                  Calculation: SUM_SELECTED
                  {overlapCapable &&
                    " — adds qualifying mentions and may exceed 100%."}
                </Typography>
              </Box>
            );
          })}
        </Box>
      )}

      {disabled || addDisabled ? (
        (disabledMessage || addDisabledMessage) && (
          <Alert severity="info" sx={{ fontSize: "0.875rem" }}>
            {disabledMessage || addDisabledMessage}
          </Alert>
        )
      ) : isBuilderOpen ? (
        <CategoryRollupBuilder
          barOptions={barOptions}
          existingNames={existingNames}
          claimedSourceLabels={claimedSourceLabels}
          claimedSourceLabelToRollupName={claimedSourceLabelToRollupName}
          ineligibleSourceLabels={ineligibleSourceLabels}
          name={builderName}
          selectedSourceLabels={builderSelectedLabels}
          onNameChange={setBuilderName}
          onSelectionChange={setBuilderSelectedLabels}
          onCancel={resetBuilder}
          onCreate={handleSave}
          isEditing={!!editingRollupId}
          itemLabel={itemLabel}
          nameLabel={nameLabel}
        />
      ) : (
        <Tooltip
          title={
            minSelectionMessage ??
            `At least two source ${itemLabel} are required to create a rollup.`
          }
          disableHoverListener={eligibleBarCount >= 2}
          disableFocusListener={eligibleBarCount >= 2}
          disableTouchListener={eligibleBarCount >= 2}
        >
          <span>
            <HPButton
              variant="outlined"
              size="small"
              startIcon={<AddIcon />}
              onClick={handleOpenBuilder}
              disabled={eligibleBarCount < 2}
            >
              Add New Rollup
            </HPButton>
          </span>
        </Tooltip>
      )}
    </Box>
  );

  if (!bordered) {
    return (
      <Box sx={{ display: "flex", flexDirection: "column" }}>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          {title}
        </Typography>
        {content}
      </Box>
    );
  }

  return (
    <Accordion
      defaultExpanded
      sx={{ boxShadow: "none", border: "1px solid #eee" }}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          {title}
        </Typography>
      </AccordionSummary>
      <AccordionDetails>{content}</AccordionDetails>
    </Accordion>
  );
};

export default CategoryRollupsSection;
